import { Copy } from "lucide-react";
import {
  useGetIdentity,
  useNotify,
  useShowContext,
  useTranslate,
} from "ra-core";
import { useState, type ReactNode } from "react";
import { Show } from "@/components/admin/show";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { ListCardsSkeleton } from "../design/ListStates";
import { APP_LOCALE, NUMERIC_DATE_FORMAT } from "../misc/locale";
import { formatMxn } from "../quoter/PlanSchedule";
import type { AppRole } from "../types";
import type { ReceivableSummary } from "./invoicingTypes";
import { canCopyConcept } from "./invoicingScreenRules";
import { ReceivableActions } from "./ReceivableActions";

const EMPTY = "—";
const COPIED_FEEDBACK_MS = 2000;

const formatDate = (iso?: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString(APP_LOCALE, NUMERIC_DATE_FORMAT)
    : EMPTY;

const Fact = ({ label, children }: { label: string; children: ReactNode }) => (
  <div>
    <dt className="text-xs text-muted-foreground">{label}</dt>
    <dd className="text-sm tabular-nums">{children}</dd>
  </div>
);

/** The frozen concept and its "Copiar concepto" button (available from `por_facturar` onward). */
const ConceptCard = ({ receivable }: { receivable: ReceivableSummary }) => {
  const translate = useTranslate();
  const notify = useNotify();
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(receivable.concept_text ?? "");
      notify("crm.invoicing.receivables.concept_copied", { type: "success" });
      setCopied(true);
      window.setTimeout(() => setCopied(false), COPIED_FEEDBACK_MS);
    } catch {
      notify("crm.invoicing.receivables.concept_copy_error", {
        type: "error",
      });
    }
  };

  return (
    <Card>
      <CardContent className="space-y-3">
        <h3 className="font-semibold">
          {translate("crm.invoicing.receivables.concept_title")}
        </h3>
        {receivable.concept_text ? (
          <p
            data-testid="frozen-concept"
            className="rounded-md border bg-muted/40 p-3 text-sm break-words"
          >
            {receivable.concept_text}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            {translate("crm.invoicing.receivables.concept_pending")}
          </p>
        )}
        {canCopyConcept(receivable) ? (
          <Button
            type="button"
            variant="outline"
            className="h-11 sm:h-9"
            onClick={copy}
          >
            <Copy className="size-4" aria-hidden="true" />
            {translate(
              copied
                ? "crm.invoicing.receivables.copied"
                : "crm.invoicing.receivables.copy_concept",
            )}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
};

const ReceivableShowLayout = () => {
  const { record, isPending } = useShowContext<ReceivableSummary>();
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  if (isPending || !record || !identity) return <ListCardsSkeleton rows={3} />;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-xl font-semibold tabular-nums">
              {record.operation_id} ·{" "}
              {translate("crm.invoicing.fields.installment_n", {
                n: record.installment_number,
              })}
            </h2>
            <span
              data-testid="receivable-status"
              className="inline-flex min-h-7 items-center rounded-full border px-3 text-sm font-medium"
            >
              {translate(`crm.invoicing.status.receivable.${record.status}`)}
            </span>
          </div>
          <dl className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <Fact label={translate("crm.invoicing.fields.project")}>
              {record.project_name ?? EMPTY}
            </Fact>
            <Fact label={translate("crm.invoicing.fields.unit")}>
              {record.unit_identifier ?? EMPTY}
            </Fact>
            <Fact label={translate("crm.invoicing.fields.client")}>
              {record.client_name ?? EMPTY}
            </Fact>
            <Fact label={translate("crm.invoicing.fields.advisor")}>
              {record.advisor_name ?? EMPTY}
            </Fact>
            <Fact label={translate("crm.invoicing.fields.payer")}>
              {record.payer_name ??
                translate(`crm.invoicing.payer_type.${record.payer_type}`)}
            </Fact>
            <Fact label={translate("crm.invoicing.fields.sale_value")}>
              {record.sale_value == null ? EMPTY : formatMxn(record.sale_value)}
            </Fact>
            <Fact label={translate("crm.invoicing.fields.commission_pct")}>
              {record.commission_pct == null
                ? EMPTY
                : `${record.commission_pct}%`}
            </Fact>
            <Fact label={translate("crm.invoicing.fields.subtotal")}>
              {formatMxn(record.subtotal)}
            </Fact>
            <Fact
              label={translate("crm.invoicing.fields.iva", {
                pct: record.iva_pct,
              })}
            >
              {formatMxn(record.iva_amount)}
            </Fact>
            <Fact label={translate("crm.invoicing.fields.total")}>
              <span className="font-semibold">
                {formatMxn(record.total_amount)}
              </span>
            </Fact>
            <Fact label={translate("crm.invoicing.fields.invoice_folio")}>
              <span data-testid="invoice-folio">
                {record.invoice_folio ?? EMPTY}
              </span>
            </Fact>
            <Fact label={translate("crm.invoicing.fields.collected_at")}>
              {formatDate(record.collected_at)}
            </Fact>
          </dl>
          {record.last_rejection_reason ? (
            <p className="text-sm text-[#B3321C]" data-testid="last-rejection">
              {translate("crm.invoicing.vobo.last_rejection", {
                reason: record.last_rejection_reason,
              })}
            </p>
          ) : null}
          {record.cancel_reason ? (
            <p className="text-sm text-muted-foreground">
              {translate("crm.invoicing.fields.cancel_reason")}:{" "}
              {record.cancel_reason}
            </p>
          ) : null}
          <ReceivableActions
            receivable={record}
            role={identity.role as AppRole}
          />
        </CardContent>
      </Card>
      <ConceptCard receivable={record} />
    </div>
  );
};

/**
 * Almikia Epic E6 (TASK-040): detail of a receivable (direccion, contabilidad,
 * auditoria). Shows only the receivable's own text snapshots: no link into the
 * operations or clients screens, which contabilidad cannot open.
 */
export const ReceivableShow = () => (
  <Show actions={false}>
    <ReceivableShowLayout />
  </Show>
);
